/**
 * Official HYROX Station Metadata & Silhouette Badges
 */
export const STATION_META = [
  { key: 'SkiErg',             label: 'SkiErg',             iconFile: 'skierg.png',            accent: '#38bdf8', isDistance: true  },
  { key: 'Sled Push',          label: 'Sled Push',          iconFile: 'sledpush.png',          accent: '#f97316', isDistance: true  },
  { key: 'Sled Pull',          label: 'Sled Pull',          iconFile: 'sledpull.png',          accent: '#a78bfa', isDistance: true  },
  { key: 'Burpee Broad Jumps', label: 'Burpee Broad Jumps', iconFile: 'burpeebroadjumps.png',  accent: '#f43f5e', isDistance: false },
  { key: 'Rowing',             label: 'Rowing',             iconFile: 'rowing.png',            accent: '#34d399', isDistance: true  },
  { key: 'Farmers Carry',      label: "Farmer's Carry",     iconFile: 'farmerscarry.png',      accent: '#facc15', isDistance: true  },
  { key: 'Sandbag Lunges',     label: 'Sandbag Lunges',     iconFile: 'sandbaglunges.png',     accent: '#fb923c', isDistance: true  },
  { key: 'Wall Balls',         label: 'Wall Balls',         iconFile: 'wallballs.png',         accent: '#e879f9', isDistance: false },
];

export const CANONICAL_HYROX_STATIONS = [
  'SkiErg',
  'Sled Push',
  'Sled Pull',
  'Burpee Broad Jumps',
  'Rowing',
  'Farmers Carry',
  'Sandbag Lunges',
  'Wall Balls'
];

export const HYROX_STATIONS = [
  "Skier",
  "Sled Push",
  "Sled Pull",
  "Burpee Broad Jumps",
  "Rower",
  "Farmer's Carry",
  "Walking SBag Lunges",
  "Wall Balls",
  "RoxZone"
];

const GENERIC_STAGE_NAMES = new Set([
  'station',
  'strength',
  'roxkit station',
  'hyrox station',
  'functional/station',
  'functional strength training',
  'traditional strength training',
  'unknown',
  'other',
  'work',
  'lap'
]);

export function isGenericStageName(name) {
  if (!name || typeof name !== 'string') return true;
  const cleaned = name.trim().toLowerCase();
  if (!cleaned) return true;
  return GENERIC_STAGE_NAMES.has(cleaned);
}

const EXACT_STATION_ALIASES = new Map([
  ['roxzone', 'RoxZone'],
  ['rox zone', 'RoxZone'],
  ['roxzones', 'RoxZone'],
  ['run', 'Run'],
  ['running', 'Run'],
  ['rest', 'Rest'],
  ['rest period', 'Rest'],
  ['cardio', 'Cardio'],
  ['skierg', 'SkiErg'],
  ['ski erg', 'SkiErg'],
  ['skier', 'SkiErg'],
  ['sled push', 'Sled Push'],
  ['sledpush', 'Sled Push'],
  ['sled pull', 'Sled Pull'],
  ['sledpull', 'Sled Pull'],
  ['burpee broad jumps', 'Burpee Broad Jumps'],
  ['burpee broad jump', 'Burpee Broad Jumps'],
  ['burpeebroadjumps', 'Burpee Broad Jumps'],
  ['rowing', 'Rowing'],
  ['rower', 'Rowing'],
  ['row', 'Rowing'],
  ['farmers carry', 'Farmers Carry'],
  ["farmer's carry", 'Farmers Carry'],
  ['farmer carry', 'Farmers Carry'],
  ['farmerscarry', 'Farmers Carry'],
  ['sandbag lunges', 'Sandbag Lunges'],
  ['sandbag lunge', 'Sandbag Lunges'],
  ['walking sbag lunges', 'Sandbag Lunges'],
  ['walking sandbag lunges', 'Sandbag Lunges'],
  ['sbag lunges', 'Sandbag Lunges'],
  ['wall balls', 'Wall Balls'],
  ['wall ball', 'Wall Balls'],
  ['wallballs', 'Wall Balls'],
  ['wallball', 'Wall Balls']
]);

function normalizeParentStationToken(rawToken) {
  const token = String(rawToken || '').trim();
  if (!token) return '';
  const lower = token.toLowerCase().replace(/\s+/g, ' ');
  if (EXACT_STATION_ALIASES.has(lower)) {
    return EXACT_STATION_ALIASES.get(lower);
  }
  if (/^run(?:\s+\d+)?$/i.test(token)) return 'Run';
  if (/^rox\s*zone\b/i.test(token)) return 'RoxZone';
  return token;
}

export function normalizeStationName(rawName) {
  if (!rawName || typeof rawName !== 'string') return '';
  let cleaned = rawName
    .replace(/^[\s🏃💪⚡⏳☕🚴🏋️‍♂️🏋️]+/u, '')
    .trim()
    .replace(/^(?:stage\s*\d+\s*[:.\-)]?\s*|\d+\s*[.:\-)]\s*)/i, '')
    .trim()
    .replace(/^[\s🏃💪⚡⏳☕🚴🏋️‍♂️🏋️]+/u, '')
    .trim()
    .replace(/^station\s*:\s*/i, '')
    .trim()
    .replace(/\s*\(\d+(?:\.\d+)?\s*(?:m|km|reps|cal|kcal|min|s)\)\s*$/i, '')
    .trim();

  if (!cleaned) return '';

  if (cleaned.includes(':')) {
    const colonIdx = cleaned.indexOf(':');
    const parentPart = cleaned.slice(0, colonIdx).trim();
    const altPart = cleaned.slice(colonIdx + 1).trim();
    const normParent = normalizeParentStationToken(parentPart);
    if (isGenericStageName(normParent)) {
      return normalizeParentStationToken(altPart);
    }
    return altPart ? `${normParent}: ${altPart}` : normParent;
  }

  return normalizeParentStationToken(cleaned);
}

function isRecognizedStageToken(token) {
  if (!token) return false;
  const norm = normalizeStationName(token);
  const base = norm.includes(':') ? norm.split(':')[0].trim() : norm;
  return (
    CANONICAL_HYROX_STATIONS.includes(base) ||
    ['Run', 'RoxZone', 'Rest', 'Cardio', 'Station', 'Strength'].includes(base)
  );
}

export function parseSequentialStationsFromNotes(notesText) {
  if (!notesText || typeof notesText !== 'string') return [];
  const lines = notesText.split(/\r?\n/).map(l => l.trim()).filter(Boolean);
  if (lines.length === 0) return [];

  const arrowLines = lines.filter(l => l.includes('➔') || l.includes('->'));
  if (arrowLines.length > 0) {
    const tokens = [];
    for (const line of arrowLines) {
      const parts = line.split(/\s*(?:➔|->)\s*/);
      for (const part of parts) {
        const norm = normalizeStationName(part);
        if (norm) tokens.push(norm);
      }
    }
    return tokens;
  }

  if (lines.length >= 2 && lines.every(isRecognizedStageToken)) {
    return lines.map(normalizeStationName).filter(Boolean);
  }

  return [];
}

export function isRawStationSequenceNotes(notesText) {
  if (!notesText || typeof notesText !== 'string') return false;
  const trimmed = notesText.trim();
  if (!trimmed) return false;
  if (!trimmed.includes('➔') && !trimmed.includes('->')) return false;
  const lines = trimmed.split(/\r?\n/).map(l => l.trim()).filter(Boolean);
  if (lines.length !== 1) return false;
  const parts = lines[0].replace(/\.$/, '').split(/\s*(?:➔|->)\s*/).filter(Boolean);
  return parts.length >= 2 && parts.some(isRecognizedStageToken);
}

export function resolveBestWorkoutNotes(meta = {}, dbw = {}) {
  const safeMeta = meta && typeof meta === 'object' ? meta : {};
  const safeDbw = dbw && typeof dbw === 'object' ? dbw : {};
  const candidateNotes = safeMeta.notes !== undefined
    ? safeMeta.notes
    : (safeDbw.notes !== undefined ? safeDbw.notes : undefined);
  const candidateAiSummary = safeMeta.aiSummary || safeDbw.aiSummary || null;

  if (
    candidateAiSummary &&
    typeof candidateAiSummary === 'string' &&
    candidateAiSummary.trim() !== '' &&
    !isRawStationSequenceNotes(candidateAiSummary) &&
    (!candidateNotes || isRawStationSequenceNotes(candidateNotes))
  ) {
    return candidateAiSummary;
  }

  if (candidateNotes !== undefined) {
    return candidateNotes;
  }
  if (candidateAiSummary) {
    return candidateAiSummary;
  }
  return 'Synced from cloud';
}

export function getStationIconUrl(stationKey) {
  const norm = String(stationKey || '').toLowerCase().replace(/[^a-z0-9]/g, '');
  if (norm.includes('ski')) return 'assets/stations/skierg.png';
  if (norm.includes('push')) return 'assets/stations/sledpush.png';
  if (norm.includes('pull')) return 'assets/stations/sledpull.png';
  if (norm.includes('burpee')) return 'assets/stations/burpeebroadjumps.png';
  if (norm.includes('row')) return 'assets/stations/rowing.png';
  if (norm.includes('farmer')) return 'assets/stations/farmerscarry.png';
  if (norm.includes('sandbag') || norm.includes('lunge')) return 'assets/stations/sandbaglunges.png';
  if (norm.includes('wall') || norm.includes('ball')) return 'assets/stations/wallballs.png';
  if (norm.includes('run')) return 'assets/stations/run.png';
  if (norm.includes('rox')) return 'assets/stations/roxzone.png';
  return 'assets/stations/run.png';
}

export function getStationAccent(stationKey) {
  const meta = STATION_META.find(m => m.key === stationKey || m.label === stationKey);
  if (meta) return meta.accent;
  const norm = String(stationKey || '').toLowerCase();
  if (norm.includes('ski')) return '#38bdf8';
  if (norm.includes('push')) return '#f97316';
  if (norm.includes('pull')) return '#a78bfa';
  if (norm.includes('burpee')) return '#f43f5e';
  if (norm.includes('row')) return '#34d399';
  if (norm.includes('farmer')) return '#facc15';
  if (norm.includes('sandbag') || norm.includes('lunge')) return '#fb923c';
  if (norm.includes('wall') || norm.includes('ball')) return '#e879f9';
  if (norm.includes('run')) return '#60a5fa';
  if (norm.includes('rox')) return '#fbbf24';
  return '#6366f1';
}

export function getStationBadgeHtml(stationKey, sizeClass = '') {
  const url = getStationIconUrl(stationKey);
  const accent = getStationAccent(stationKey);
  return `<span class="station-badge ${sizeClass}" style="--st-accent:${accent};"><span class="station-glyph" style="-webkit-mask-image:url('${url}');mask-image:url('${url}');"></span></span>`;
}

